import { clearSettings } from './settings'

/**
 * Every call to our server goes through here, so the token is attached in one place and a token the
 * server no longer accepts drops the browser back to the sign in screen instead of leaving it staring at
 * a screen that quietly stopped updating.
 */
const TOKEN_KEY = 'auth_token'
const COOKIE_DOMAIN = '.oddspulse.org'
const TEN_YEARS = 10 * 365 * 24 * 60 * 60

const readCookie = () => {
  const hit = document.cookie.split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${TOKEN_KEY}=`))
  return hit ? decodeURIComponent(hit.slice(TOKEN_KEY.length + 1)) : null
}

export const getToken = () => {
  const fromCookie = readCookie()
  if (fromCookie) {
    return fromCookie
  }
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch (e) {
    return null
  }
}

export const setToken = (token) => {
  // the cookie is scoped to the parent domain so the apex and the demo subdomain share one sign in;
  // on localhost or a bare IP that domain is rejected, hence the plain fallback
  const secure = window.location.protocol === 'https:' ? '; secure' : ''
  const onOddspulse = window.location.hostname.endsWith('oddspulse.org')
  const domain = onOddspulse ? `; domain=${COOKIE_DOMAIN}` : ''
  document.cookie = `${TOKEN_KEY}=${encodeURIComponent(token)}${domain}; path=/; max-age=${TEN_YEARS}; samesite=lax${secure}`
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch (e) {}
}

export const clearToken = () => {
  // the settings cache paints the first frame, so leaving it behind shows the next person to sign in
  // this browser the previous account's thresholds and hidden leagues
  clearSettings()
  const onOddspulse = window.location.hostname.endsWith('oddspulse.org')
  const domain = onOddspulse ? `; domain=${COOKIE_DOMAIN}` : ''
  document.cookie = `${TOKEN_KEY}=${domain}; path=/; max-age=0`
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch (e) {}
}

export const login = async (host, username, password) => {
  const response = await fetch(`${host}/api/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || 'Sign in failed')
  }
  setToken(data.token)
  return data
}

export const logout = async (host) => {
  const token = getToken()
  clearToken()
  if (token) {
    await fetch(`${host}/api/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {})
  }
}

/**
 * A 401 means the token is gone or was never any good. Dropping it and reloading is the only honest
 * response: everything on screen from here on would be stale, and the polling would fail silently.
 */
export const apiFetch = async (host, path, options = {}) => {
  const token = getToken()
  const response = await fetch(`${host}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      /**
       * Which screen this came from. The server sees the same feed request whether it is the search
       * board, the upcoming board or a league grid, and one poll every two seconds says nothing about
       * where anybody actually is. This costs a header and answers that.
       */
      'X-Screen': window.location.pathname.slice(0, 80)
    }
  })

  if (response.status === 401) {
    clearToken()
    window.location.reload()
    throw new Error('Not signed in')
  }
  return response
}

export const apiJson = async (host, path, options) => {
  const response = await apiFetch(host, path, options)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || `Request failed with ${response.status}`)
  }
  return data
}
